/** RFC 0279 S4: the same public registry a person and an agent browse. */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  type AppDoc,
  type AppSummary,
  type IconSpec,
  type PackageRelease,
  type RegistryEntry,
  type RegistryPackage,
  registryClient,
} from '@mod/core/model'
import { ArrowLeft, ArrowRight, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { IconTile } from '../../services/apps/AppIcon'
import { useDocuments } from '../../services/world/repo/useDocument'
import { PackageSource } from './PackageSource'

const registry = registryClient({ getAuthToken: () => '' })
/** Stable, so the Source tab's fetch runs once per open, not per render. */
const loadSource = (sourceId: string) => registry.source(sourceId)

export function DiscoverPage({
  lineageId,
  onSelect,
  onInstall,
  onAsk,
  installedApps,
}: {
  lineageId: string | null
  onSelect: (lineageId: string | null) => void
  onInstall: (
    release: PackageRelease,
    releaseId: string,
    scope: 'world' | 'project',
  ) => Promise<void>
  onAsk: (text: string) => Promise<void>
  installedApps: AppSummary[]
}) {
  const [entries, setEntries] = useState<RegistryEntry[]>([])
  /** What the registry returned for the current search, or null when none is set. */
  const [results, setResults] = useState<RegistryEntry[] | null>(null)
  const [featured, setFeatured] = useState<RegistryEntry[]>([])
  const [item, setItem] = useState<RegistryPackage | null>(null)
  const [release, setRelease] = useState<PackageRelease | null>(null)
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('All')
  const [scope, setScope] = useState<'world' | 'project'>('world')
  const [loading, setLoading] = useState(false)
  const [installing, setInstalling] = useState(false)
  const [installed, setInstalled] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [worldUnavailable, setWorldUnavailable] = useState(false)
  /** RFC 0279 S10: the package page's tab. Source is fetched only on open. */
  const [tab, setTab] = useState<'overview' | 'source'>('overview')
  const appDocs = useDocuments<AppDoc>(
    installedApps.map((app) => app.id as AnyDocumentId),
  )
  const installedLineages = new Map<string, AppSummary>()
  for (const app of installedApps) {
    const doc = appDocs.get(app.id)
    for (const lineageId of [
      doc?.installedFrom?.lineageId,
      doc?.publishedAs?.lineageId,
    ]) {
      if (lineageId) installedLineages.set(lineageId, app)
    }
  }

  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    Promise.all([registry.list(), registry.featured()])
      .then(([all, picks]) => {
        if (!live) return
        setEntries(all)
        setFeatured(picks)
      })
      .catch((cause: unknown) => {
        if (live) setError(String(cause))
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [])

  // The registry searches, not the page. The unfiltered list is the newest page
  // only, so filtering it here would hide every older package from a person
  // while an agent, which sends its query, still finds it.
  useEffect(() => {
    const q = query.trim()
    const wanted = category === 'All' ? undefined : category
    if (!q && !wanted) {
      setResults(null)
      return
    }
    let live = true
    const timer = setTimeout(() => {
      registry
        .list(q, wanted)
        .then((found) => {
          if (live) setResults(found)
        })
        .catch((cause: unknown) => {
          if (live) setError(String(cause))
        })
    }, 200)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [query, category])

  useEffect(() => {
    setTab('overview')
    if (!lineageId) {
      setItem(null)
      setRelease(null)
      return
    }
    let live = true
    setLoading(true)
    setError(null)
    registry
      .package(lineageId)
      .then(async (details) => {
        const bytes = await registry.release(details.latest.releaseId)
        if (!live) return
        setItem(details)
        setRelease(bytes)
      })
      .catch((cause: unknown) => {
        if (live) setError(String(cause))
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [lineageId])

  const categories = useMemo(
    () => ['All', ...new Set(entries.map((e) => e.category).filter(Boolean))],
    [entries],
  )
  const shown = results ?? entries
  const spotlight = featured.length > 0 ? featured : entries.slice(0, 3)
  const installedApp = item ? installedLineages.get(item.lineageId) : undefined
  const typeOwner = release?.dataType
    ? installedApps.find((app) => app.dataType === release.dataType)
    : undefined
  const installBlocked = Boolean(
    installedApp || typeOwner || installed === item?.lineageId,
  )

  const install = async () => {
    if (!item || !release || installing) return
    setInstalling(true)
    setError(null)
    setWorldUnavailable(false)
    try {
      await onInstall(release, item.latest.releaseId, scope)
      setInstalled(item.lineageId)
    } catch (cause) {
      setError(String(cause))
      setWorldUnavailable(
        cause instanceof Error &&
          cause.name === 'WorldInstallProjectUnavailableError',
      )
    } finally {
      setInstalling(false)
    }
  }

  const ask = async () => {
    if (!query.trim()) return
    setError(null)
    try {
      await onAsk(query.trim())
    } catch (cause) {
      setError(String(cause))
    }
  }

  return (
    <section className="flex h-full w-full min-w-0 flex-col bg-background text-foreground">
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-8 sm:px-8 lg:px-12">
        {error && (
          <div role="alert" className="mb-5 text-red-500 text-sm">
            <p>{error}</p>
            {worldUnavailable && (
              <button
                type="button"
                onClick={() => {
                  setScope('project')
                  setError(null)
                  setWorldUnavailable(false)
                }}
                className="mt-2 rounded-lg border border-current px-3 py-1.5"
              >
                Choose This project
              </button>
            )}
          </div>
        )}
        {lineageId ? (
          item && release ? (
            <div className="w-full">
              <button
                type="button"
                onClick={() => onSelect(null)}
                className="mb-7 flex items-center gap-2 text-muted-foreground text-xs"
              >
                <ArrowLeft size={13} /> Back to Discover
              </button>
              <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_260px]">
                <main>
                  <div className="mb-8 flex items-center gap-4">
                    <PackageTile
                      lineageId={item.lineageId}
                      name={item.name}
                      icon={item.icon ?? release.icon}
                      size={64}
                      className="rounded-2xl text-2xl"
                    />
                    <div>
                      <h1 className="font-semibold text-2xl">{item.name}</h1>
                      <p className="text-muted-foreground text-sm">
                        {item.description}
                      </p>
                      <p className="mt-1 text-muted-foreground text-xs">
                        {item.publisher.name} · {item.kind} · {item.category} ·{' '}
                        {item.latest.version}
                      </p>
                    </div>
                  </div>
                  {release.sourceReleaseId && (
                    <div
                      role="tablist"
                      className="mb-7 flex gap-6 border-border border-b pb-3"
                    >
                      {(['overview', 'source'] as const).map((name) => (
                        <button
                          key={name}
                          type="button"
                          role="tab"
                          aria-selected={tab === name}
                          onClick={() => setTab(name)}
                          className={`relative text-xs ${tab === name ? 'text-foreground after:absolute after:inset-x-0 after:-bottom-[13px] after:h-0.5 after:bg-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                          {name === 'overview' ? 'Overview' : 'Source'}
                        </button>
                      ))}
                    </div>
                  )}
                  {release.sourceReleaseId && tab === 'source' ? (
                    <PackageSource
                      sourceId={release.sourceReleaseId}
                      load={loadSource}
                      installedApps={installedApps}
                    />
                  ) : (
                    <Overview item={item} release={release} />
                  )}
                </main>
                <aside className="border-border border-l pl-6 text-xs">
                  <button
                    type="button"
                    onClick={() => void install()}
                    disabled={installing || installBlocked}
                    className="w-full rounded-lg bg-foreground px-4 py-2 text-background disabled:opacity-50"
                  >
                    {installedApp || installed === item.lineageId
                      ? 'Installed'
                      : typeOwner
                        ? 'Type already in use'
                        : installing
                          ? 'Installing…'
                          : 'Install'}
                  </button>
                  {installedApp && (
                    <p className="mt-2 text-muted-foreground">
                      {installedApp.scope === 'world'
                        ? 'Available in every project.'
                        : 'Available in this project.'}
                    </p>
                  )}
                  {!installedApp && typeOwner && (
                    <p className="mt-2 text-muted-foreground">
                      {typeOwner.name} already uses this record type.
                    </p>
                  )}
                  <p className="mt-5 mb-2 text-muted-foreground">For</p>
                  <div className="flex gap-2">
                    {(['world', 'project'] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        aria-pressed={scope === option}
                        onClick={() => setScope(option)}
                        className={`rounded-full border px-2 py-1 ${scope === option ? 'border-foreground' : 'border-border text-muted-foreground'}`}
                      >
                        {option === 'world' ? 'Every project' : 'This project'}
                      </button>
                    ))}
                  </div>
                  <h2 className="mt-6 mb-2 text-muted-foreground">
                    It can reach
                  </h2>
                  <p className="leading-relaxed">
                    Writes:{' '}
                    {release.capabilities.writes.join(', ') || 'nothing'}
                    <br />
                    Reads: {release.capabilities.reads.join(', ') || 'nothing'}
                  </p>
                  <h2 className="mt-6 mb-2 text-muted-foreground">It opens</h2>
                  <p>
                    {release.dataType ??
                      release.handles?.join(', ') ??
                      'No records'}
                  </p>
                  <h2 className="mt-6 mb-2 text-muted-foreground">Versions</h2>
                  {item.versions.map((v) => (
                    <p key={v.releaseId} className="py-1">
                      {v.version} ·{' '}
                      {new Date(v.publishedAt).toLocaleDateString()}
                    </p>
                  ))}
                  <h2 className="mt-6 mb-2 text-muted-foreground">Publisher</h2>
                  <p>{item.publisher.name}</p>
                  {/* RFC 0279 S9: forking writes files to disk, which the
                    window cannot do, so the page names the command rather
                    than offering a button that would do nothing. */}
                  <h2 className="mt-6 mb-2 text-muted-foreground">Source</h2>
                  {release.sourceReleaseId ? (
                    <p>
                      Included, read it under Source. Fork it with{' '}
                      <code className="break-all text-xs">
                        mod-next app fork {item.lineageId} --dir &lt;folder&gt;
                      </code>
                    </p>
                  ) : (
                    <p>Not published</p>
                  )}
                </aside>
              </div>
            </div>
          ) : loading ? (
            <p className="text-muted-foreground text-sm">Loading package…</p>
          ) : null
        ) : (
          <div className="w-full">
            <p className="text-muted-foreground text-[11px] uppercase tracking-[0.16em]">
              The package registry / Mod
            </p>
            <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
              <h1 className="mt-2 font-semibold text-4xl leading-none tracking-[-0.045em] sm:text-5xl">
                Make Mod yours.
              </h1>
              <p className="max-w-[260px] text-muted-foreground text-sm leading-relaxed">
                Find a way to work, made by someone who already tried it.
              </p>
            </div>
            <div className="flex min-h-14 items-center gap-3 rounded-xl border border-border bg-muted/40 px-4">
              <Search size={21} className="shrink-0" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (
                    event.key === 'Enter' &&
                    query.trim().split(/\s+/).length >= 4
                  )
                    void ask()
                }}
                placeholder="Find an app, or describe what you need"
                aria-label="Search packages"
                className="min-w-0 flex-1 bg-transparent text-base outline-none"
              />
              <button
                type="button"
                onClick={() => void ask()}
                aria-label="Ask your agent"
                className="grid size-8 shrink-0 place-items-center rounded-lg bg-foreground text-background"
              >
                <ArrowRight size={16} />
              </button>
            </div>
            <p className="mt-2 text-muted-foreground text-xs">
              Search by name, or press Enter to ask your agent with a sentence.
            </p>
            {spotlight.length > 0 && !query && (
              <section className="mt-10">
                <div className="mb-4 flex items-baseline justify-between gap-4">
                  <h2 className="font-semibold text-lg tracking-tight">
                    {featured.length > 0
                      ? 'Chosen for you'
                      : 'New in the registry'}
                  </h2>
                  <span className="text-muted-foreground text-xs">
                    {spotlight.length}{' '}
                    {spotlight.length === 1 ? 'place' : 'places'} to start
                  </span>
                </div>
                <div
                  className={`grid min-h-[310px] gap-3 ${spotlight.length > 1 ? 'lg:grid-cols-[1.55fr_1fr]' : ''}`}
                >
                  <FeatureCard
                    entry={spotlight[0]}
                    large
                    installed={installedLineages.has(spotlight[0].lineageId)}
                    onClick={() => onSelect(spotlight[0].lineageId)}
                  />
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                    {spotlight.slice(1, 3).map((entry) => (
                      <FeatureCard
                        key={entry.lineageId}
                        entry={entry}
                        installed={installedLineages.has(entry.lineageId)}
                        onClick={() => onSelect(entry.lineageId)}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}
            <section className="mt-10">
              <div className="mb-5 flex flex-wrap gap-6 border-border border-b pb-3">
                {categories.map((name) => (
                  <button
                    key={name}
                    type="button"
                    aria-pressed={category === name}
                    onClick={() => setCategory(name)}
                    className={`relative text-xs ${category === name ? 'text-foreground after:absolute after:inset-x-0 after:-bottom-[13px] after:h-0.5 after:bg-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
              <div className="mb-4 flex items-baseline justify-between gap-4">
                <h2 className="font-semibold text-lg tracking-tight">
                  Browse the registry
                </h2>
                <span className="text-muted-foreground text-xs">
                  {shown.length} {shown.length === 1 ? 'package' : 'packages'} ·
                  newest first
                </span>
              </div>
              {loading && (
                <p className="text-muted-foreground text-sm">
                  Loading registry…
                </p>
              )}
              {!loading && shown.length === 0 && (
                <p className="text-muted-foreground text-sm">
                  {entries.length === 0
                    ? 'No packages have been published yet.'
                    : 'No packages match.'}
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {shown.map((entry) => (
                  <PackageCard
                    key={entry.lineageId}
                    entry={entry}
                    installed={installedLineages.has(entry.lineageId)}
                    onClick={() => onSelect(entry.lineageId)}
                  />
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </section>
  )
}

/** What a package is for, from its page: the tab a package opens on. */
function Overview({
  item,
  release,
}: {
  item: RegistryPackage
  release: PackageRelease
}) {
  return (
    <>
      {item.screenshots.length > 0 && (
        <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {item.screenshots.map((url) => (
            <img
              key={url}
              src={url}
              alt={`${item.name} screenshot`}
              className="aspect-video w-full rounded-xl border border-border object-cover"
            />
          ))}
        </div>
      )}
      <h2 className="mb-2 font-medium text-sm">About</h2>
      <p className="mb-7 whitespace-pre-wrap text-muted-foreground text-sm leading-relaxed">
        {item.about || item.description}
      </p>
      {release.skill && (
        <>
          <h2 className="mb-2 font-medium text-sm">Its skill</h2>
          <pre className="mb-7 whitespace-pre-wrap rounded-xl border border-border bg-muted/40 p-4 font-sans text-muted-foreground text-xs leading-relaxed">
            {release.skill.content}
          </pre>
        </>
      )}
      {release.tools && release.tools.length > 0 && (
        <>
          <h2 className="mb-2 font-medium text-sm">Tools</h2>
          {release.tools.map((tool) => (
            <p key={tool.name} className="border-border border-b py-2 text-xs">
              <code className="mr-3">{tool.name}</code>
              <span className="text-muted-foreground">{tool.description}</span>
            </p>
          ))}
        </>
      )}
    </>
  )
}

/**
 * A package's tile: its icon, drawn by the renderer the window uses for an
 * installed app, or its first letter when it was published without one.
 */
function PackageTile({
  lineageId,
  name,
  icon,
  size,
  className,
}: {
  lineageId: string
  name: string
  icon: IconSpec | null | undefined
  size: number
  className: string
}) {
  const app = useMemo(() => ({ id: lineageId, name }), [lineageId, name])
  if (icon) return <IconTile spec={icon} app={app} size={size} />
  return (
    <div
      data-testid="letter-tile"
      style={{ width: size, height: size }}
      className={`grid shrink-0 place-items-center bg-muted font-semibold ${className}`}
    >
      {name[0]}
    </div>
  )
}

function PackageCard({
  entry,
  installed,
  onClick,
}: {
  entry: RegistryEntry
  installed: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-24 gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:bg-muted/40"
    >
      <PackageTile
        lineageId={entry.lineageId}
        name={entry.name}
        icon={entry.icon}
        size={40}
        className="rounded-lg"
      />
      <div className="min-w-0">
        <h3 className="font-medium text-sm">{entry.name}</h3>
        <p className="mt-1 line-clamp-2 text-muted-foreground text-xs">
          {entry.description}
        </p>
        <p className="mt-2 text-muted-foreground text-[11px]">
          {entry.publisher.name} · {entry.category}
        </p>
        {installed && <p className="mt-2 text-[11px]">Installed ✓</p>}
      </div>
    </button>
  )
}

function FeatureCard({
  entry,
  large = false,
  installed,
  onClick,
}: {
  entry: RegistryEntry
  large?: boolean
  installed: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative isolate flex min-h-36 overflow-hidden rounded-2xl border border-border bg-muted text-left transition-colors hover:border-foreground/40 ${large ? 'min-h-[310px] p-6' : 'p-5'}`}
    >
      {entry.screenshots[0] && (
        <img
          src={entry.screenshots[0]}
          alt=""
          className="absolute inset-0 -z-10 size-full object-cover opacity-35 transition-transform group-hover:scale-[1.02]"
        />
      )}
      <span className="absolute inset-0 -z-10 bg-gradient-to-t from-background via-background/80 to-transparent" />
      <span className="flex w-full flex-col items-start">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          {large ? 'Spotlight' : 'From the registry'} · {entry.category}
        </span>
        <span
          className={`mt-4 font-semibold tracking-tight ${large ? 'text-3xl' : 'text-xl'}`}
        >
          {entry.name}
        </span>
        <span className="mt-2 max-w-80 text-muted-foreground text-sm leading-relaxed">
          {entry.description}
        </span>
        <span className="mt-auto pt-5 text-xs">
          {installed ? 'Installed ✓' : `Explore ${entry.name} ↗`}
        </span>
      </span>
    </button>
  )
}
